/**
 * E07-F08 — Jev decision provider: shared contracts.
 *
 * Spec: docs/superpowers/specs/2026-10-20-research-jev-triage-design.md, §5.2 «Jev — провайдер
 * решений» (+ §5.7 for the admin shapes). Jev answers QUESTIONS with probabilities, it does not
 * write text — so it is NOT an `LlmProvider`. The question format follows the standard's working
 * client `.claude/scripts/jev_decide.py`:
 *
 *   { model, state, questions: { <name>: { type: noul|choice|score, instructions, criteria } } }
 *
 *   - `noul`   — yes/no; criteria is EXACTLY `{ true, false }`;
 *   - `choice` — pick one; criteria is `{ <option>: <description> }`, ≥ 2 options;
 *   - `score`  — graded; criteria is a LIST of ≥ 2 anchored levels (low → high), no bare numbers.
 *
 * Questions and criteria are English (the model's language; also the calibration invariant:
 * request data goes into `state`, never into the question text).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------
export const JEV_QUESTION_TYPES = ['noul', 'choice', 'score'];
export const jevQuestionTypeSchema = z.enum(JEV_QUESTION_TYPES);
/** One question as stored and as sent on the wire. Shape rules: {@link validateJevQuestionSet}. */
export const jevQuestionSchema = z.object({
    type: jevQuestionTypeSchema,
    instructions: z.string().max(2000),
    /** noul/choice: label → description; score: anchored levels, low → high. */
    criteria: z.union([z.record(z.string(), z.string().max(1000)), z.array(z.string().max(1000))]),
});
/** Questions keyed by name — a whole set goes out in ONE request. */
export const jevQuestionsSchema = z.record(z.string().min(1).max(60), jevQuestionSchema);
// ---------------------------------------------------------------------------
// Bands (answers → bucket). Same condition grammar as jev_decide.py apply_bands.
// ---------------------------------------------------------------------------
/** Condition on one question's answer. noul/score: lte/gte on the value; choice: winner/top. */
export const jevBandConditionSchema = z
    .object({
    lte: z.number().optional(),
    gte: z.number().optional(),
    winner_in: z.array(z.string()).optional(),
    top_gte: z.number().optional(),
    confidence_gte: z.number().optional(),
})
    .strict();
/** Cross-check of a choice against a control noul (lint: a 'none'-like option needs one). */
export const jevBandAgreeSchema = z
    .object({
    choice: z.string(),
    noul: z.string(),
    positive: z.union([z.string(), z.array(z.string())]),
    gte: z.number().optional(),
    lte: z.number().optional(),
})
    .strict();
export const jevBandSchema = z
    .object({
    name: z.string().min(1).max(40),
    /** The fallback band; exactly one, and it must be the last. */
    default: z.boolean().optional(),
    /** question name → condition; plus the reserved `agree` key (list of cross-checks). */
    when: z
        .record(z.string(), z.union([jevBandConditionSchema, z.array(jevBandAgreeSchema)]))
        .optional(),
})
    .strict();
const CYRILLIC = /[Ѐ-ӿ]/;
const BARE_NUMBER = /^\s*[-+]?\d+(\.\d+)?\s*$/;
const COMPOUND = /\b(?:and|or) whether\b/i;
const NONE_LIKE = new Set(['none', 'nothing', 'other', 'not_applicable', 'n_a']);
function normLabel(s) {
    return s
        .replace(/[\s_-]+/g, ' ')
        .trim()
        .toLowerCase();
}
/**
 * Validate a question set's shape. Returns the list of issues; an empty list = valid.
 *
 * Rules (spec §5.7 «теми же правилами, что jev_decide.py lint»):
 *   - at least one question; type is noul|choice|score; instructions non-empty, one judgement;
 *   - noul: criteria exactly {true, false}; choice: ≥ 2 options; score: a list of ≥ 2 levels,
 *     each an anchor sentence, not a bare number; no empty criterion, no criterion that just
 *     repeats its label;
 *   - English only: no Cyrillic in instructions or criteria;
 *   - bands (when given): exactly one default band and it is the last; conditions refer to
 *     known questions; a choice with a 'none'-like option needs a control noul in an `agree`.
 */
export function validateJevQuestionSet(input) {
    const issues = [];
    const add = (code, detail) => issues.push({ code, detail });
    const qs = input.questions ?? {};
    const names = Object.keys(qs);
    if (names.length === 0)
        add('no-questions', 'set has no questions');
    for (const q of names) {
        const d = qs[q] ?? {};
        const t = d.type;
        const instr = typeof d.instructions === 'string' ? d.instructions.trim() : '';
        const crit = d.criteria;
        if (t !== 'noul' && t !== 'choice' && t !== 'score') {
            add('bad-type', `${q}: type ${JSON.stringify(t)} is not noul|choice|score`);
            continue;
        }
        if (!instr)
            add('empty-instructions', `${q}: instructions are empty`);
        if ((instr.match(/\?/g) ?? []).length > 1 || COMPOUND.test(instr)) {
            add('compound-question', `${q}: one question = one judgement`);
        }
        if (CYRILLIC.test(instr))
            add('not-english', `${q}: instructions must be in English`);
        let texts = [];
        if (t === 'noul') {
            const ok = crit !== null &&
                typeof crit === 'object' &&
                !Array.isArray(crit) &&
                Object.keys(crit).length === 2 &&
                'true' in crit &&
                'false' in crit;
            if (!ok)
                add('noul-criteria', `${q}: criteria must be exactly {true, false}`);
            else
                texts = Object.entries(crit);
        }
        else if (t === 'choice') {
            if (!crit ||
                typeof crit !== 'object' ||
                Array.isArray(crit) ||
                Object.keys(crit).length < 2) {
                add('choice-options', `${q}: choice needs >= 2 options`);
            }
            else
                texts = Object.entries(crit);
        }
        else {
            if (!Array.isArray(crit) || crit.length < 2) {
                add('score-criteria', `${q}: score needs a list of >= 2 anchored levels`);
            }
            else {
                texts = crit.map((v, i) => [String(i), v]);
                for (const [, v] of texts) {
                    if (BARE_NUMBER.test(String(v)))
                        add('score-bare-number', `${q}: level ${JSON.stringify(v)} is a bare number`);
                }
            }
        }
        for (const [k, v] of texts) {
            const text = typeof v === 'string' ? v.trim() : '';
            if (!text) {
                add('empty-criterion', `${q}.${k}: empty`);
                continue;
            }
            if (CYRILLIC.test(text))
                add('not-english', `${q}.${k}: criteria must be in English`);
            const n = normLabel(text);
            if (n === normLabel(k) || (t === 'noul' && ['true', 'false', 'yes', 'no'].includes(n))) {
                add('criterion-repeats-label', `${q}.${k}: criterion repeats the label`);
            }
        }
    }
    const bands = input.bands;
    if (bands !== undefined) {
        const defaults = bands.filter((b) => b?.default === true).length;
        if (defaults !== 1 || (bands.length > 0 && bands[bands.length - 1]?.default !== true)) {
            add('bands-default', 'exactly one default band, and it must be the last one');
        }
        const agreed = new Set();
        for (const b of bands) {
            const when = (b?.when ?? {});
            for (const [key, cond] of Object.entries(when)) {
                if (key === 'agree') {
                    for (const a of (Array.isArray(cond) ? cond : [])) {
                        if (a?.choice)
                            agreed.add(a.choice);
                        if (!a?.choice || !(a.choice in qs) || !a?.noul || !(a.noul in qs)) {
                            add('band-unknown-question', `band ${String(b?.name)}: agree refers to unknown questions`);
                        }
                    }
                    continue;
                }
                if (!(key in qs))
                    add('band-unknown-question', `band ${String(b?.name)}: unknown question ${key}`);
            }
        }
        for (const q of names) {
            const d = qs[q];
            if (d?.type === 'choice' &&
                d.criteria &&
                typeof d.criteria === 'object' &&
                !Array.isArray(d.criteria)) {
                if (Object.keys(d.criteria).some((k) => NONE_LIKE.has(k)) && !agreed.has(q)) {
                    add('choice-without-control', `${q}: a 'none'-like option needs a control noul in a band agree`);
                }
            }
        }
    }
    return issues;
}
// ---------------------------------------------------------------------------
// E07-F08-T03 — question sets, threshold versions, decisions (spec §5.2, §5.6, §5.7)
// ---------------------------------------------------------------------------
/** The three seeded sets (spec §5.3 / §5.4 / §5.5). */
export const JEV_SET_KEYS = [
    'research_triage',
    'research_verdict',
    'research_same_company',
];
/** State truncation limits of a set, by name (characters / item counts). */
export const jevStateCapsSchema = z.record(z.string().min(1).max(60), z.number().int().nonnegative());
/**
 * Named numeric thresholds of a set (e.g. `likelyJunkMinConfidence`, `needsHumanGradeConfidence`).
 * `null` = the rule is OFF (spec §5.3: «вероятный мусор» is not used before calibration).
 */
export const jevThresholdsSchema = z.record(z.string().min(1).max(60), z.number().nullable());
/** Where a threshold version came from. */
export const JEV_THRESHOLD_SOURCES = ['default', 'auto', 'manual', 'rollback'];
export const jevThresholdVersionSchema = z.object({
    id: z.number().int(),
    version: z.number().int(),
    /** The set version these thresholds were computed for. */
    setVersion: z.number().int(),
    thresholds: jevThresholdsSchema,
    source: z.enum(JEV_THRESHOLD_SOURCES),
    isCurrent: z.boolean(),
    note: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: z.string(),
});
/** One stored question set (GET /admin/jev/question-sets[/:setKey]). */
export const jevQuestionSetSchema = z.object({
    id: z.number().int(),
    setKey: z.string(),
    displayName: z.string(),
    description: z.string().nullable(),
    questions: jevQuestionsSchema,
    bands: z.array(jevBandSchema),
    stateCaps: jevStateCapsSchema,
    /** Thresholds a set returns to when its questions change (the safe values). */
    defaultThresholds: jevThresholdsSchema,
    /** Bumped on every change of questions / bands / stateCaps. */
    version: z.number().int(),
    /** False after any question edit until re-calibrated on labels (spec §5.6). */
    calibrated: z.boolean(),
    thresholdsFrozen: z.boolean(),
    /**
     * E07-F10-T05 — set by a question edit: the free auto-recompute is off (likely junk stays off)
     * until the admin re-runs Jev on the labelled rows. Optional for older payloads.
     */
    needsRecalibration: z.boolean().optional(),
    /** E07-F10-T03 — the safety check's last warning; null = the last recompute passed. */
    calibrationWarning: z.string().nullable().optional(),
    /** E07-F10-T03 — what the last recompute saw (samples, positives, …). */
    calibrationStats: z.record(z.string(), z.unknown()).nullable().optional(),
    calibrationCheckedAt: z.string().nullable().optional(),
    isActive: z.boolean(),
    currentThresholds: jevThresholdVersionSchema.nullable(),
    updatedBy: z.string().nullable(),
    /** E07-F12-T02 — the display name of `updatedBy` (route-resolved; null = unknown user). */
    updatedByName: z.string().nullable().optional(),
    updatedAt: z.string(),
});
export const jevQuestionSetListResponseSchema = z.object({
    success: z.literal(true).optional(),
    sets: z.array(jevQuestionSetSchema),
});
/**
 * PUT /admin/jev/question-sets/:setKey — partial update. The resulting set is shape-checked by
 * {@link validateJevQuestionSet}; a 400 `JEV_SET_INVALID` carries the issues.
 */
export const jevQuestionSetUpdateSchema = z
    .object({
    displayName: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    questions: jevQuestionsSchema.optional(),
    bands: z.array(jevBandSchema).optional(),
    stateCaps: jevStateCapsSchema.optional(),
    defaultThresholds: jevThresholdsSchema.optional(),
})
    .strict();
/** Status of a stored decision: `unavailable` rows are kept too (they were journaled/paid). */
export const JEV_DECISION_STATUSES = ['ok', 'unavailable'];
/** Per-question stored answer (the calibration input, spec §5.6 «Метки»). */
export const jevStoredAnswerSchema = z.object({
    type: jevQuestionTypeSchema,
    value: z.number().nullable(),
    choice: z.string().nullable(),
    probabilities: z.record(z.string(), z.number()),
    confidence: z.number().nullable(),
});
// ---------------------------------------------------------------------------
// E07-F10 — human labels, reference labels, threshold recompute, recalibration (spec §5.6)
// ---------------------------------------------------------------------------
/** Where a label came from: a person in Review, or Ksenia's reference lists (wins on conflict). */
export const JEV_LABEL_SOURCES = ['review', 'reference'];
/**
 * What the person did. `undo_status` = an accept/reject toggled back to pending; `undo_prefilter`
 * = a «вернуть» of a prefilter flag was itself undone. An undo withdraws every active label of its
 * family (status | prefilter).
 */
export const JEV_LABEL_ACTIONS = [
    'accept',
    'reject',
    'undo_status',
    'grade',
    'restore_junk',
    'restore_prefilter',
    'undo_prefilter',
    'manual_check',
    'reference',
];
/** The label's meaning for calibration; null = withdrawn (an undo). */
export const JEV_LABEL_VERDICTS = ['positive', 'negative', 'not_junk'];
export const jevLabelSchema = z.object({
    id: z.string(),
    researchCompanyId: z.number().int(),
    requestId: z.number().int(),
    /** The row's latest Jev decision at label time (`jev_decisions.id` as a string), if any. */
    decisionId: z.string().nullable(),
    source: z.enum(JEV_LABEL_SOURCES),
    action: z.enum(JEV_LABEL_ACTIONS),
    verdict: z.enum(JEV_LABEL_VERDICTS).nullable(),
    grade: z.string().nullable(),
    fromGrade: z.string().nullable(),
    reason: z.string().nullable(),
    companyName: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: z.string(),
});
/** POST /admin/jev/reference-labels/import — per list and in total. */
export const jevReferenceImportListSchema = z.object({
    list: z.string(),
    clientKey: z.string(),
    /** Requests whose title matched the list. */
    requests: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative(),
    positives: z.number().int().nonnegative(),
    gradeC: z.number().int().nonnegative(),
    /** Request rows matched by domain or normalized name. */
    matchedPositives: z.number().int().nonnegative(),
    matchedGradeC: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    /** Matched rows whose review label disagrees (the reference wins). */
    conflictsWithReview: z.number().int().nonnegative(),
});
export const jevReferenceImportResponseSchema = z.object({
    success: z.literal(true).optional(),
    lists: z.array(jevReferenceImportListSchema),
    matchedPositives: z.number().int().nonnegative(),
    matchedGradeC: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    conflictsWithReview: z.number().int().nonnegative(),
});
/** One safety-check violation (spec §5.6 «предохранитель»). */
export const jevSafetyViolationSchema = z.object({
    code: z.enum(['positive_in_junk', 'junk_below_min_positives', 'downgraded_ab_passes']),
    threshold: z.string(),
    researchCompanyId: z.number().int().nullable(),
    detail: z.string(),
});
/** Result of one threshold recompute (free arithmetic, no Jev call). */
export const jevRecomputeOutcomeSchema = z.object({
    success: z.literal(true).optional(),
    setKey: z.string(),
    /** applied = a new version is current; unchanged = same thresholds; rejected = safety check failed. */
    status: z.enum(['applied', 'unchanged', 'rejected', 'skipped']),
    /** skipped: frozen | needs_recalibration | inactive | no_thresholds | not_found. */
    reason: z.string().nullable(),
    thresholds: jevThresholdsSchema.nullable(),
    candidate: jevThresholdsSchema.nullable(),
    violations: z.array(jevSafetyViolationSchema),
    warning: z.string().nullable(),
    stats: z.record(z.string(), z.unknown()).nullable(),
    versionId: z.number().int().nullable(),
});
/** PUT /admin/jev/question-sets/:setKey/thresholds/freeze */
export const jevThresholdFreezeBodySchema = z.object({ frozen: z.boolean() }).strict();
/** POST /admin/jev/question-sets/:setKey/thresholds/rollback — any past version of the set. */
export const jevThresholdRollbackBodySchema = z
    .object({ versionId: z.number().int().positive() })
    .strict();
/** GET /admin/jev/question-sets/:setKey/recalibration/preview — what the button will cost. */
export const jevRecalibrationPreviewSchema = z.object({
    success: z.literal(true).optional(),
    setKey: z.string(),
    setVersion: z.number().int(),
    needsRecalibration: z.boolean(),
    /** Labelled rows Jev judged with this set before. */
    labelledRows: z.number().int().nonnegative(),
    /** Of those, the rows whose stored call state can be re-sent (= Jev calls the run makes). */
    rows: z.number().int().nonnegative(),
    /** Labelled rows without a re-sendable stored state (truncated / missing journal). */
    skippedNoState: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
    estimatedCostCents: z.number().nonnegative(),
    priceIsFallbackEstimate: z.boolean(),
    priceSampleSize: z.number().int().nonnegative(),
});
/** POST …/recalibration — `confirmedRows` must equal the preview's `rows` (409 otherwise). */
export const jevRecalibrationBodySchema = z
    .object({ confirmedRows: z.number().int().nonnegative() })
    .strict();
export const jevRecalibrationRunSchema = z.object({
    success: z.literal(true).optional(),
    setKey: z.string(),
    runId: z.string(),
    status: z.enum(['running', 'done', 'failed']),
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    outcome: jevRecomputeOutcomeSchema.nullable(),
    error: z.string().nullable(),
});
/** GET …/recalibration/latest — the set's last run in this process (null = none since start). */
export const jevRecalibrationLatestResponseSchema = z.object({
    success: z.literal(true).optional(),
    run: jevRecalibrationRunSchema.nullable(),
});
// ---------------------------------------------------------------------------
// E07-F08-T02 — Jev key, on/off switch and the AI-check verdict toggle (admin, «Настройки ИИ»)
// ---------------------------------------------------------------------------
/**
 * Which path produces AI-check verdicts (spec §5.4 «Тумблер отката»). Default `jev`. Changed ONLY
 * by an admin through PUT /admin/jev/settings — there is no automatic switch on errors.
 */
export const JEV_VERDICT_ENGINES = ['jev', 'haiku'];
export const jevVerdictEngineSchema = z.enum(JEV_VERDICT_ENGINES);
export const JEV_DEFAULT_VERDICT_ENGINE = 'jev';
/**
 * GET /admin/jev/settings. The key NEVER crosses the wire — only `hasKey` + a masked tail.
 * `configured` = enabled AND a usable key: the UI hides/disables Jev buttons otherwise
 * («Jev не настроен»).
 */
export const jevSettingsResponseSchema = z.object({
    success: z.literal(true).optional(),
    enabled: z.boolean(),
    hasKey: z.boolean(),
    maskedKey: z.string().nullable(),
    configured: z.boolean(),
    verdictEngine: jevVerdictEngineSchema,
    /** Pinned model snapshot (read-only). */
    model: z.string(),
    updatedBy: z.string().nullable(),
    /** E07-F12-T01 — the display name of `updatedBy` (route-resolved; null = unknown user). */
    updatedByName: z.string().nullable().optional(),
    updatedAt: z.string().nullable(),
});
/**
 * PUT /admin/jev/settings — partial update. `apiKey` is tri-state like the LLM slots:
 * omitted = keep, '' = clear, non-empty = encrypt & store.
 */
export const jevSettingsUpdateSchema = z
    .object({
    apiKey: z.string().max(500).optional(),
    enabled: z.boolean().optional(),
    verdictEngine: jevVerdictEngineSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// E07-F09-T06 — reconciliation run «Jev vs Haiku» on Ksenia's reference rows (spec §5.4
// «Сверка перед продом»). Admin-only, started by an explicit confirmed click; both verdict
// paths judge the SAME reference rows; the real rows are never changed.
// ---------------------------------------------------------------------------
export const JEV_RECONCILE_GRADES = ['A', 'B', 'C'];
/** GET /admin/jev/reconcile/preview — rows and price BEFORE the run (read-only, no AI call). */
export const jevReconcilePreviewSchema = z.object({
    success: z.literal(true).optional(),
    /** Reference rows the run will judge (= the number to confirm). */
    rows: z.number().int().nonnegative(),
    byReferenceGrade: z.object({
        A: z.number().int().nonnegative(),
        B: z.number().int().nonnegative(),
        C: z.number().int().nonnegative(),
    }),
    lists: z.array(z.object({ list: z.string(), rows: z.number().int().nonnegative() })),
    /** Matched rows left out: the reference entry has no grade (a bare positive list). */
    skippedNoGrade: z.number().int().nonnegative(),
    /** Matched rows left out: no website (AI-check refuses them on both paths). */
    skippedNoWebsite: z.number().int().nonnegative(),
    /** Jev is switched on with a usable key (else the run is refused with 409). */
    jevConfigured: z.boolean(),
    /** The admin toggle as it is now (the run does NOT change it). */
    verdictEngine: jevVerdictEngineSchema,
    costPerRowUsd: z.object({ jev: z.number().nonnegative(), haiku: z.number().nonnegative() }),
    estimatedCostUsd: z.number().nonnegative(),
    priceIsFallbackEstimate: z.boolean(),
    /** A reconciliation run is in progress (a second start is refused with 409). */
    running: z.boolean(),
});
/** POST /admin/jev/reconcile — `confirmedRows` must equal the preview's `rows` (409 otherwise). */
export const jevReconcileBodySchema = z
    .object({ confirmedRows: z.number().int().positive() })
    .strict();
const reconcileGradeSchema = z.enum(JEV_RECONCILE_GRADES);
/** One path's result on one reference row. */
export const jevReconcileEngineRowSchema = z.object({
    /** scored | failed | skipped (e.g. excluded state — no paid call). */
    status: z.enum(['scored', 'failed', 'skipped']),
    grade: z.string().nullable(),
    needsHuman: z.boolean(),
    /** Criteria (of 6) with verdict «unclear». */
    unclear: z.number().int().nonnegative(),
    error: z.string().nullable(),
    costUsd: z.number().nonnegative(),
    /** The price is the recent average, not the call's own price. */
    costIsEstimate: z.boolean().optional(),
});
export const jevReconcileRowSchema = z.object({
    rowId: z.number().int(),
    requestId: z.number().int(),
    list: z.string(),
    name: z.string(),
    domain: z.string().nullable(),
    referenceGrade: reconcileGradeSchema,
    haiku: jevReconcileEngineRowSchema.nullable(),
    jev: jevReconcileEngineRowSchema.nullable(),
});
/** One path's totals over the run's rows. */
export const jevReconcileEngineSummarySchema = z.object({
    rows: z.number().int().nonnegative(),
    scored: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** Rows whose grade equals Ksenia's (a failed / ungraded row is a miss). */
    gradeMatches: z.number().int().nonnegative(),
    gradeMatchRate: z.number().min(0).max(1),
    /** Reference A rows this path graded C (exit criterion: Jev must have none). */
    referenceAGotC: z.number().int().nonnegative(),
    /** Share of criterion verdicts «unclear» over the scored rows (6 per row). */
    unclearShare: z.number().min(0).max(1),
    /** Share of scored rows marked «нужна проверка человеком». */
    needsHumanShare: z.number().min(0).max(1),
    /** reference grade → this path's grade (`none` = no grade / failed) → rows. */
    confusion: z.record(z.string(), z.record(z.string(), z.number().int().nonnegative())),
    costUsd: z.number().nonnegative(),
    /** Haiku calls are priced by the recent average, not by an invoice. */
    costIsEstimate: z.boolean(),
});
export const jevReconcileRunSchema = z.object({
    success: z.literal(true).optional(),
    id: z.number().int(),
    status: z.enum(['running', 'done', 'failed']),
    totalRows: z.number().int().nonnegative(),
    doneRows: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    createdBy: z.string().nullable(),
    error: z.string().nullable(),
    /** Null until the run is done. */
    summary: z
        .object({
        haiku: jevReconcileEngineSummarySchema,
        jev: jevReconcileEngineSummarySchema,
        /** Exit criterion: Jev match ≥ Haiku match AND no reference A got C from Jev. */
        passed: z.boolean(),
        verdict: z.string(),
    })
        .nullable(),
    rows: z.array(jevReconcileRowSchema),
});
/** GET /admin/jev/reconcile/latest — the last saved run (null = never run). */
export const jevReconcileLatestResponseSchema = z.object({
    success: z.literal(true).optional(),
    run: jevReconcileRunSchema.nullable(),
});
// ---------------------------------------------------------------------------
// E07-F12-T04 — 30-day metrics + «check a set on one row» (admin, spec §5.7)
// ---------------------------------------------------------------------------
/** Metrics of one set over the window (from `jev_decisions` + `jev_labels`). */
export const jevSetMetricsSchema = z.object({
    setKey: z.string(),
    /** Every journaled decision of the set in the window (ok + unavailable). */
    decisions: z.number().int().nonnegative(),
    /** Rows decided in the window that carry an effective human label (positive/negative/not_junk). */
    labels: z.number().int().nonnegative(),
    /** Labelled rows where Jev agreed with the human / labelled rows Jev could be compared on. */
    agreement: z.number().min(0).max(1).nullable(),
    /** research_triage: rows whose latest ok decision fell into `likely_junk`, 0..1. */
    junkShare: z.number().min(0).max(1),
    /** research_verdict: rows whose latest ok decision needs a human check, 0..1. */
    needsHumanShare: z.number().min(0).max(1),
    /** Sum of `cost_usd` of the window's decisions. */
    costUsd: z.number().nonnegative(),
});
/** GET /admin/jev/metrics?days=30 */
export const jevMetricsResponseSchema = z.object({
    success: z.literal(true).optional(),
    days: z.number().int().positive(),
    fromDate: z.string(),
    toDate: z.string(),
    sets: z.array(jevSetMetricsSchema),
});
/** One request row the set check can be run on (Jev judged it before, its state is re-sendable). */
export const jevCheckRowSchema = z.object({
    /** research_companies.id */
    id: z.number().int(),
    requestId: z.number().int(),
    requestTitle: z.string(),
    company: z.string(),
    domain: z.string(),
    city: z.string(),
    state: z.string(),
    employees: z.string(),
    /** The sets with a stored state for this row (others answer 409 CHECK_NO_STATE). */
    setKeys: z.array(z.string()),
});
/** GET /admin/jev/check-rows */
export const jevCheckRowsResponseSchema = z.object({
    success: z.literal(true).optional(),
    rows: z.array(jevCheckRowSchema),
});
/** GET /admin/jev/question-sets/:setKey/check-quote — the price of ONE call (no AI call). */
export const jevCheckQuoteSchema = z.object({
    success: z.literal(true).optional(),
    setKey: z.string(),
    costUsd: z.number().nonnegative(),
    isEstimate: z.boolean(),
});
/** POST /admin/jev/question-sets/:setKey/check ← { rowId } */
export const jevCheckBodySchema = z.object({ rowId: z.number().int().positive() }).strict();
/** One answer as the admin sees it: the top answer + probability per option / level. */
export const jevCheckAnswerSchema = z.object({
    question: z.string(),
    kind: jevQuestionTypeSchema,
    /** noul: `yes` | `no`; choice: the option; score: the level. */
    answer: z.string(),
    confidence: z.number(),
    probabilities: z.array(z.object({ key: z.string(), p: z.number() })),
});
/** The result of one check — shown, NEVER written to the row. */
export const jevCheckResultSchema = z.object({
    success: z.literal(true).optional(),
    setKey: z.string(),
    rowId: z.number().int(),
    answers: z.array(jevCheckAnswerSchema),
    /** Triage: the bucket; same_company: the band; verdict: the grade band (`graded`, …). */
    band: z.string().nullable(),
    /** research_verdict only: the grade would need a human check (null for other sets). */
    needsHuman: z.boolean().nullable(),
    reason: z.string(),
    costUsd: z.number().nonnegative(),
    latencyMs: z.number().int().nonnegative(),
    stateChars: z.number().int().nonnegative(),
    aiCallLogId: z.string().nullable(),
});
//# sourceMappingURL=jev.js.map